/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { SelectProps } from '../../../types/InputComponentProps';
import { Select, createListCollection, Portal, Separator, Box, VStack } from 'chakra-v3';
import { InputContainer, selectTriggerStyles, selectContentStyles, selectItemBaseStyles } from './FormInputContainer';
import { ButtonSelect } from './ButtonSelect';
import { useMemo } from 'react';
import { Text } from '../../../../../components/Index';

export function SelectInput(props: SelectProps) {
  const {
    id,
    description,
    label,
    error,
    isInvalid,
    isDisabled,
    isRequired,
    options,
    size: _size,
    value,
    onChange,
  } = props;
  const numKeys = Object.keys(options).length;
  const isUseButtons = numKeys < 5;

  const collection = useMemo(
    () =>
      createListCollection({
        items: Object.entries(options).map(([value, label]) => ({
          label,
          value,
        })),
      }),
    [options],
  );

  const selectedLabel = value ? options[value as keyof typeof options] : '';

  return (
    <InputContainer
      id={id}
      description={description}
      label={label}
      error={error}
      isInvalid={isInvalid}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      {!isUseButtons && (
        <Select.Root
          collection={collection}
          disabled={isDisabled}
          invalid={isInvalid}
          value={typeof value === 'string' && value ? [value] : []}
          onValueChange={(val) => onChange(id!, val.value[0] || '')}
          closeOnSelect
        >
          <Select.HiddenSelect />
          <Select.Control>
            <Select.Trigger {...selectTriggerStyles}>
              <Select.ValueText placeholder="Select option" color={value ? 'brand.white' : 'text.placeholder'}>
                {selectedLabel}
              </Select.ValueText>
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Portal>
            <Select.Positioner>
              <Select.Content {...selectContentStyles}>
                <VStack gap="0" align="stretch">
                  {collection.items.map((item, index) => {
                    const isChecked = value === item.value;
                    return (
                      <Box key={item.value}>
                        <Select.Item
                          item={item}
                          {...selectItemBaseStyles}
                          cursor={isDisabled ? 'not-allowed' : 'pointer'}
                          _hover={{
                            bg: isDisabled ? 'brand.tealDark' : 'select.hover',
                          }}
                        >
                          <Box
                            w="20px"
                            h="20px"
                            borderRadius="50%"
                            borderWidth="2px"
                            borderStyle="solid"
                            borderColor={isChecked ? 'brand.secondaryBlue2' : 'brand.white'}
                            bg={isChecked ? 'brand.cta' : 'transparent'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                          >
                            {isChecked && <Box w="8px" h="8px" borderRadius="50%" bg="brand.secondaryBlue2" />}
                          </Box>
                          <Text variant="body" color={isChecked ? 'brand.secondaryBlue2' : 'brand.white'} flex="1 0 0">
                            {item.label}
                          </Text>
                          <Select.ItemIndicator display="none" />
                        </Select.Item>
                        {index < collection.items.length - 1 && (
                          <Separator borderColor="select.separator" borderWidth="1px" h="0" m="0" />
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      )}
      {isUseButtons && (
        <ButtonSelect
          options={options}
          value={value as keyof typeof options}
          onChange={(val) => onChange(id!, val ?? '')}
          isDisabled={isDisabled}
        />
      )}
    </InputContainer>
  );
}
